import axios from 'axios'
import React, { useEffect, useState, useRef } from 'react'
import { Form, InputGroup, Tabs, Tab, Alert } from 'react-bootstrap'
import { getValidPreviousIntentId } from '../../../../utils/functions/getValidPreviousIntentId'
import { updateInstagramIntent } from '../../../../utils/functions/updateInstagramIntent'

interface IProps {
  intent: any
  inputJson: any[]
  setInputJson: React.Dispatch<React.SetStateAction<any[]>>
  closeModal: () => void
  data: any
}

const FILE_UPLOAD_URL = process.env.REACT_APP_FILE_UPLOAD_URL

const Image: React.FC<IProps> = ({ intent, inputJson, setInputJson, closeModal, data }) => {
  const intentIndex = inputJson.findIndex((item) => item.id === intent.id)
  const [key, setKey] = useState('url')
  const [fileName, setFileName] = useState<string>('No file chosen')
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([])
  const [imageUrl, setImageUrl] = useState(intent?.message?.attachment?.payload?.url || '')
  const [intentDescription, setIntentDescription] = useState<string>(
    intent?.intentDescription || ''
  )
  const [isSaving, setIsSaving] = useState(false)
  const maxLength = 5000000 // 5MB
  const fileInputRef = useRef<HTMLInputElement>(null)
  const urlInputRef = useRef<HTMLInputElement>(null)

  const formatSizeInMB = (size: number) => {
    return (size / (1024 * 1024)).toFixed(2) + ' MB'
  }

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files: any = event.target.files
    let validFiles: File[] = []

    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        if (files[i].size <= maxLength) {
          validFiles.push(files[i])
          setFileName(files[i].name)
        } else {
          alert(`File ${files[i].name} exceeds the size limit ${formatSizeInMB(maxLength)}.`)
        }
      }

      if (validFiles.length > 0) {
        setImageUrl('')
        urlInputRef.current?.value && (urlInputRef.current.value = '')
        setUploadedFiles([...uploadedFiles, ...validFiles])
      }
    }
  }

  const handleFileUploadsAndUpdateJson = async () => {
    if (uploadedFiles.length > 0) {
      const uploadPromises = uploadedFiles.map(async (file) => {
        const response = await axios.post(FILE_UPLOAD_URL as string, {
          files: [{ fileName: file.name, mimeType: file.type }]
        })

        const uploadURL = response.data[0].uploadURL
        await axios.put(uploadURL, file)

        return {
          filename: response.data[0].Key,
          url: response.data[0].url
        }
      })

      const fileResponses = await Promise.all(uploadPromises)
      const uploadedUrl = fileResponses[fileResponses.length - 1].url // Use latest uploaded file's URL
      setImageUrl(uploadedUrl)

      const newInputJson = [...inputJson]
      newInputJson[intentIndex] = {
        ...intent,
        intentDescription,
        message: {
          attachment: {
            type: 'image',
            payload: {
              url: uploadedUrl
            }
          }
        }
      }
      setInputJson(newInputJson)
    }
  }

  useEffect(() => {
    if (uploadedFiles.length > 0) {
      handleFileUploadsAndUpdateJson()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedFiles])

  const updateImageLink = (link: string) => {
    const newInputJson = [...inputJson]
    newInputJson[intentIndex] = {
      ...intent,
      intentDescription,
      message: {
        attachment: {
          type: 'image',
          payload: {
            url: link
          }
        }
      }
    }
    setInputJson(newInputJson)
  }

  const handleUrlBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (value && value !== imageUrl) {
      fileInputRef.current?.value && (fileInputRef.current.value = '')
      setFileName('No file chosen')
      setUploadedFiles([])

      setImageUrl(value)
      updateImageLink(value)
    }
  }

  const handleSave = async () => {
    const token = localStorage.getItem('auth-token')
    const instagramIDString = data?.instagramIDString
    const previousIntentId = getValidPreviousIntentId(
      intentIndex,
      inputJson,
      data?.WhatsAppInput || []
    )
    setIsSaving(true)
    await updateInstagramIntent({
      token,
      instagramIDString,
      intent,
      intentJson: inputJson[intentIndex],
      previousIntentId,
      onSuccess: closeModal
    })
  }

  const handleTabChange = (k: string | null) => {
    setKey(k || 'url')
  }

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setIntentDescription(value)

    const newInputJson = [...inputJson]
    newInputJson[intentIndex] = {
      ...intent,
      ...newInputJson[intentIndex],
      intentDescription: value,
      message: {
        attachment: {
          type: 'image',
          payload: {
            url: imageUrl
          }
        }
      }
    }
    setInputJson(newInputJson)
  }

  return (
    <>
      <h5 className='card-title h4 mb-4'>Image Message</h5>

      <Form.Group className='mb-3'>
        <Form.Label>Intent Description</Form.Label>
        <Form.Control
          type='text'
          placeholder='Enter description...'
          value={intentDescription}
          onChange={handleDescriptionChange}
        />
      </Form.Group>

      <Tabs id='image-input-tabs' activeKey={key} onSelect={handleTabChange} className='mb-3'>
        <Tab eventKey='url' title='From URL'>
          <InputGroup className='my-3'>
            <InputGroup.Text>Image URL</InputGroup.Text>
            <Form.Control
              ref={urlInputRef}
              type='url'
              placeholder='https://example.com/image.jpg'
              defaultValue={imageUrl}
              onBlur={handleUrlBlur}
            />
          </InputGroup>
        </Tab>

        <Tab eventKey='upload' title='Upload Image'>
          <Form.Group className='my-3'>
            <Form.Label>Select image to upload</Form.Label>
            <Form.Control
              ref={fileInputRef}
              type='file'
              accept='image/*'
              onChange={handleFileInputChange}
            />
            {fileName !== 'No file chosen' && (
              <div className='mt-2 text-muted'>Selected file: {fileName}</div>
            )}
          </Form.Group>
        </Tab>
      </Tabs>

      {imageUrl && (
        <div className='text-center my-4'>
          <img
            src={imageUrl}
            alt='Preview'
            style={{
              maxWidth: '100%',
              maxHeight: '300px',
              borderRadius: '8px',
              objectFit: 'contain'
            }}
          />
        </div>
      )}

      <div className='d-flex ms-auto mt-auto'>
        <button className='btn btn-secondary me-4' onClick={closeModal}>
          <i className='bi bi-eye-fill' /> Preview
        </button>
        <button className='btn btn-primary' onClick={handleSave}>
          <i className='bi bi-save' /> {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>

      <Alert variant='info' className='mt-4'>
        <strong>WhatsApp Image Guidelines:</strong>
        <ul className='mb-0'>
          <li>HTTPS image URLs are required if using links</li>
          <li>Max file size: 5MB</li>
          <li>Supported formats: JPG, PNG, GIF</li>
          <li>Uploaded image is previewed after upload</li>
        </ul>
      </Alert>
    </>
  )
}

export default Image
